import React from 'react';
import {
  CATEGORY_CONFIG,
  groupForCategory,
  isNewBoard,
  LEADERBOARD_GROUPS,
  LeaderboardCategory,
  LeaderboardGroup,
} from 'types/interfaces/leaderboard';
import './LeaderboardTabs.css';

interface LeaderboardGroupTabsProps {
  activeCategory: LeaderboardCategory;
  onSelectGroup: (group: LeaderboardGroup) => void;
  isMobile: boolean;
}

/**
 * The four group tabs. They span the full content width above the two-column
 * grid. The URL holds the board (?tab=); the group is derived from it.
 */
export const LeaderboardGroupTabs: React.FC<LeaderboardGroupTabsProps> = ({
  activeCategory,
  onSelectGroup,
  isMobile,
}) => {
  const activeGroup = groupForCategory(activeCategory);

  return (
    <div
      className={`leaderboard-groups${isMobile ? ' leaderboard-groups--mobile' : ''}`}
      role="tablist"
      aria-label="Leaderboard groups"
    >
      {LEADERBOARD_GROUPS.map((g) => (
        <button
          key={g.key}
          type="button"
          role="tab"
          aria-selected={g.key === activeGroup}
          className={`leaderboard-group-tab${g.key === activeGroup ? ' leaderboard-group-tab--active' : ''}`}
          onClick={() => onSelectGroup(g.key)}
        >
          {g.label}
        </button>
      ))}
    </div>
  );
};

interface LeaderboardBoardTabsProps {
  activeCategory: LeaderboardCategory;
  onSelectBoard: (category: LeaderboardCategory) => void;
  isMobile: boolean;
  /** The viewer's rank per board, for the mobile tabs ("Cards Sent #12"). */
  ranks?: Partial<Record<LeaderboardCategory, number | null>>;
}

/** The active group's boards, as a segmented control matching the period toggle. */
export const LeaderboardBoardTabs: React.FC<LeaderboardBoardTabsProps> = ({
  activeCategory,
  onSelectBoard,
  isMobile,
  ranks,
}) => {
  const activeGroup = groupForCategory(activeCategory);
  const group = LEADERBOARD_GROUPS.find((g) => g.key === activeGroup) ?? LEADERBOARD_GROUPS[0];

  return (
    <div
      className={`leaderboard-boards${isMobile ? ' leaderboard-boards--mobile' : ''}`}
      role="group"
      aria-label={`${group.label} boards`}
    >
      {group.boards.map((board) => {
        const active = board === activeCategory;
        const rank = ranks?.[board];
        const isNew = isNewBoard(board);
        return (
          <button
            key={board}
            type="button"
            aria-pressed={active}
            className={`leaderboard-board-tab${active ? ' leaderboard-board-tab--active' : ''}`}
            onClick={() => onSelectBoard(board)}
          >
            <span className="leaderboard-board-tab__label">{CATEGORY_CONFIG[board].label}</span>
            {isMobile && rank != null && <span className="leaderboard-board-tab__rank">#{rank}</span>}
            {isNew &&
              (isMobile ? (
                <span className="leaderboard-new-dot" aria-label="New board" />
              ) : (
                <span className="leaderboard-new-tag">NEW</span>
              ))}
          </button>
        );
      })}
    </div>
  );
};
